import { EDDA } from '@somnhold/content';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, UnsleepingState } from '../../types.ts';
import { damageHero, ev } from '../combat.ts';
import { by, crowd, held, holdable, hurt, said, standing, type Kit } from './kit.ts';

/*
 * Ser Edda, the Sworn (paladin, holy — cold white light): she swore not to let the chapel fall, and it fell. Her oath
 * halves every blow on the nightmares near her and turns part of a hero's blow back on it as light (strike her, not
 * her guard). Judgement stuns the hero who keeps its distance; the Aegis wards her guard and nothing stops her a
 * while; the Fallen Chapel is a column of white light on the closest knot of heroes that throws them and mends the
 * nightmares in it. At 33 % her oath breaks: once a fight, she kneels untouchable a moment and mends.
 */

// cds: judgement, aegis. k: untouchable ticks left, the oath already broken (1).
const JUDGE = 0;
const AEGIS = 1;
const UNTOUCHED = 0;
const BROKE = 1;

/** Judgement: cold light on the farthest hero in her reach that keeps out of her mace's. */
const judgement = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const j = EDDA.judgement;
  let tgt: Hero | null = null;
  let bd = -1;
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d > j.range * j.range || d < j.minRange * j.minRange || d <= bd) continue;
    tgt = h;
    bd = d;
  }
  if (!tgt) {
    u.cds[JUDGE] = 6;
    return;
  }
  u.cds[JUDGE] = j.every;
  hurt(s, e, u, tgt, j.damage, j.maxHpBp, 'holy');
  if (holdable(s, tgt)) {
    tgt.stun = imax(tgt.stun, j.stun);
    held(s, tgt);
  }
  ev(s, { t: 'boom', x: tgt.x, y: tgt.y, r: 700, kind: 'eddaJudgement' });
  said(s, e, u, 'Judgement', tgt.x, tgt.y);
};

/** The Aegis: a ward on every nightmare near her, and nothing stops her a while. */
const aegis = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const a = EDDA.aegis;
  let any = false;
  for (const o of s.enemies) {
    if (o === e || o.hp <= 0 || o.kind === 'boneWall' || !withinRange(e.x, e.y, o.x, o.y, a.radius)) continue;
    o.ward = imax(o.ward ?? 0, mulBp(o.maxHp, a.wardBp));
    any = true;
  }
  if (!any) {
    u.cds[AEGIS] = 6;
    return false;
  }
  u.cds[AEGIS] = a.every;
  u.unstoppable = imax(u.unstoppable, a.unstoppable);
  e.anim = 'cast';
  e.animT = 0;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: a.radius, kind: 'eddaAegis' });
  said(s, e, u, 'Aegis');
  return true;
};

/** A hero thrown `far` away from (cx, cy) (from her, standing right in its middle). */
const throwFrom = (s: BattleState, e: Enemy, h: Hero, cx: number, cy: number, far: number): void => {
  if ((h.frenzy ?? 0) > 0) return;
  let dx = h.x - cx;
  let dy = h.y - cy;
  if (dx === 0 && dy === 0) {
    dx = cx - e.x;
    dy = cy - e.y;
  }
  const d = isqrt(dx * dx + dy * dy);
  if (d === 0) return;
  const ticks = 6;
  h.shove = ticks;
  h.shovedAt = s.tick;
  h.shoveX = idiv(dx * far, d * ticks);
  h.shoveY = idiv(dy * far, d * ticks);
};

export const edda: Kit = {
  init: (_s, _e, u) => {
    u.cds = [idiv(EDDA.judgement.every, 2), idiv(EDDA.aegis.every, 2)];
    u.k = [0, 0];
  },
  // Kneeling while her broken oath holds: nothing else.
  busy: (_s, e, u) => {
    u.busy--;
    e.anim = 'cast';
    return true;
  },
  act: (s, e, u) => {
    if (u.cds[JUDGE] === 0) judgement(s, e, u);
    return u.cds[AEGIS] === 0 && aegis(s, e, u);
  },
  // The column on the closest knot of heroes within her reach.
  aim: (s, e, u) => {
    const p = EDDA.pillar;
    const c = crowd(s, e, p.range, p.radius);
    if (!c) return false;
    u.spots = [{ x: c.x, y: c.y, r: p.radius }];
    u.castA = e.facing;
    said(s, e, u, 'Pillar', c.x, c.y);
    return true;
  },
  land: (s, e, u) => {
    const p = EDDA.pillar;
    const sp = u.spots[0];
    if (!sp) return;
    for (const h of s.heroes) {
      if (!standing(h) || !withinRange(sp.x, sp.y, h.x, h.y, p.radius)) continue;
      hurt(s, e, u, h, p.damage, p.maxHpBp, 'holy');
      if (standing(h)) throwFrom(s, e, h, sp.x, sp.y, p.knock);
    }
    for (const o of s.enemies) {
      if (o === e || o.hp <= 0 || o.kind === 'boneWall' || o.hp >= o.maxHp || !withinRange(sp.x, sp.y, o.x, o.y, p.radius)) continue;
      const got = imin(o.maxHp - o.hp, mulBp(o.maxHp, p.healBp));
      o.hp += got;
      ev(s, { t: 'mend', from: e.id, id: o.id, amount: got, x: o.x, y: o.y });
    }
    ev(s, { t: 'boom', x: sp.x, y: sp.y, r: p.radius, kind: 'eddaPillar' });
  },
  strike: (s, e, h, dmg) => {
    damageHero(s, h, by(h, dmg, EDDA.blowMaxHpBp), e, 'holy', true);
  },
  // At 33 % her oath breaks (once a fight): she kneels, untouchable, and mends; a channel under way is let go.
  phase: (s, e, u) => {
    if (e.phase < 2 || (u.k[BROKE] as number) > 0) return;
    u.k[BROKE] = 1;
    u.k[UNTOUCHED] = EDDA.broken.ticks;
    // (One tick past it: the core spends a tick of it at once.)
    u.busy = EDDA.broken.ticks + 1;
    u.cast = 0;
    u.spots = [];
    u.unstoppable = imax(u.unstoppable, EDDA.broken.ticks);
    e.anim = 'cast';
    e.animT = 0;
    said(s, e, u, 'Oath');
  },
  status: (_s, e, u) => {
    if ((u.k[UNTOUCHED] as number) <= 0) return;
    u.k[UNTOUCHED] = (u.k[UNTOUCHED] as number) - 1;
    e.hp = imin(e.maxHp, e.hp + idiv(mulBp(e.maxHp, EDDA.broken.healBp), EDDA.broken.ticks));
  },
  untouchable: (_e, u) => (u.k[UNTOUCHED] as number) > 0,
  // Her oath over the nightmares near her: half of every blow, and a hero's comes back on it as light.
  guards: (s, e, _u, target, dmg, pid) => {
    const o = EDDA.oath;
    if (target.kind === 'boneWall' || !withinRange(e.x, e.y, target.x, target.y, o.radius)) return dmg;
    if (pid) {
      const h = s.heroes.find((x) => x.pid === pid);
      const back = mulBp(dmg, o.backBp);
      if (h && standing(h) && back > 0) {
        damageHero(s, h, back, e, 'holy', true);
        if (s.tick % 4 === 0) ev(s, { t: 'beam', x: target.x, y: target.y, tx: h.x, ty: h.y, kind: 'eddaOath' });
      }
    }
    return imax(1, mulBp(dmg, o.takenBp));
  },
};
